import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VideoPlayerRef } from '@kontentino/kontentino-post-previews';
import type { InstagramAudioSelection } from 'types/Attachments';
import { resolvePreviewSrc } from '../utils/resolvePreviewSrc';
import { syncAudioToVideo } from '../utils/syncAudioToVideo';
import { isAudioMixPreviewSupported } from '../utils/isAudioMixPreviewSupported';
import { useInstagramAudioTrackQuery } from './useInstagramAudioTrackQuery';
import { useInstagramAudioPublicTrackQuery } from './useInstagramAudioPublicTrackQuery';
import { DEFAULT_AUDIO_VOLUME, DEFAULT_VIDEO_VOLUME } from '../constants';

type Params = {
  pageId?: number;
  selection?: InstagramAudioSelection | null;
  enabled?: boolean;
  publicPreview?: { postId: number; token: string };
};

type PreviewVariant = 'desktop' | 'mobile';

type VideoElements = Record<PreviewVariant, HTMLVideoElement | null>;

function resolveVideoElement(
  player: VideoPlayerRef | null,
): HTMLVideoElement | null {
  const internalPlayer = player?.player?.getInternalPlayer();

  return internalPlayer instanceof HTMLVideoElement ? internalPlayer : null;
}

/**
 * `PostPreview` exposes no audio, volume or muted prop — its only hook-in is
 * the `videoPlayer` ref — so the raw `<video>` element is resolved through it
 * and paired with an audio element we own. Both previews are driven from one
 * hook so that starting one stops the other.
 */
export function usePostPreviewAudioMix({
  pageId,
  selection,
  enabled = true,
  publicPreview,
}: Params) {
  const [videoElements, setVideoElements] = useState<VideoElements>({
    desktop: null,
    mobile: null,
  });

  const audioId = selection?.audio_id ?? null;

  const canPlay = enabled && !!audioId && isAudioMixPreviewSupported();

  const trackQuery = useInstagramAudioTrackQuery({
    pageId,
    audioId,
    enabled: canPlay && !publicPreview,
  });

  const publicTrackQuery = useInstagramAudioPublicTrackQuery({
    postId: publicPreview?.postId ?? 0,
    token: publicPreview?.token ?? '',
    enabled: canPlay && !!publicPreview,
  });

  const audioSrc = canPlay
    ? resolvePreviewSrc(
        (publicPreview ? publicTrackQuery.data : trackQuery.data) ?? null,
      )
    : null;
  const audioVolume = selection?.audio_volume ?? DEFAULT_AUDIO_VOLUME;
  const videoVolume = selection?.video_volume ?? DEFAULT_VIDEO_VOLUME;

  /**
   * The package re-creates its imperative handle on every render, so React
   * flushes the callback ref with `null` in between. Only non-null elements
   * are recorded, otherwise the two would trade updates forever.
   */
  const setVideoElement = useCallback(
    (variant: PreviewVariant) => (player: VideoPlayerRef | null) => {
      const element = resolveVideoElement(player);

      if (!element) return;

      setVideoElements((previous) =>
        previous[variant] === element
          ? previous
          : { ...previous, [variant]: element },
      );
    },
    [],
  );

  const desktopRef = useMemo(
    () => setVideoElement('desktop'),
    [setVideoElement],
  );
  const mobileRef = useMemo(() => setVideoElement('mobile'), [setVideoElement]);

  useEffect(() => {
    if (!audioSrc) return;

    const mixes = [videoElements.desktop, videoElements.mobile]
      .filter((video): video is HTMLVideoElement => !!video)
      .map((video) => {
        const audio = new Audio(audioSrc);
        audio.preload = 'auto';

        return { video, audio };
      });

    const detachers = mixes.map(({ video, audio }) => {
      const stopOtherMixes = () =>
        mixes.forEach((other) => {
          if (other.video === video) return;

          other.video.pause();
          other.audio.pause();
        });

      video.addEventListener('play', stopOtherMixes);

      const detachSync = syncAudioToVideo(video, audio, {
        audioVolume,
        videoVolume,
      });

      return () => {
        video.removeEventListener('play', stopOtherMixes);
        detachSync();
        audio.pause();
        audio.src = '';
        audio.load();
      };
    });

    return () => detachers.forEach((detach) => detach());
  }, [audioSrc, audioVolume, videoVolume, videoElements]);

  return { desktopRef, mobileRef };
}
